/**
 * Hand-written contracts for the Concierge turn protocol.
 *
 * `file.ts` is nswag-generated and must not be regenerated by hand, so the new
 * fields introduced by the Concierge server live here. Everything is optional
 * and loosely typed on purpose: the server side is implemented in parallel and
 * small naming drift is expected. All tolerance for that drift is isolated in
 * `../mappers/mappers.ts`, so a rename on the server is a one-file fix.
 */

export type ConciergeSender = 'Agent' | 'User';

export type ConciergeOutcome = 'Answered' | 'Abstained' | 'Rejected' | 'ProviderError';

export interface ConciergeReceptionContact {
   phones?: string[] | null;
   emails?: string[] | null;
}

/**
 * Shape of a message as delivered over SignalR (`ReceiveMessage`) and inside
 * `GET /api/chat` history.
 *
 * The guest message and every assistant message of a turn carry the same
 * `turnId` and `clientTurnId`. A turn whose assistant reply was a
 * ProviderError may get further assistant messages when it is retried.
 */
export interface ConciergeMessageResponse {
   id?: string | null;
   sender?: ConciergeSender | string | null;
   /** The text (legacy field name, kept by the final contract). */
   message?: string | null;
   /** Alternative text field name, still accepted. */
   text?: string | null;
   createdAt?: string | Date | null;
   /** SERVER turn id. NOT equal to `clientTurnId`. */
   turnId?: string | null;
   /** The client's idempotency key for the turn (sent in `POST /api/chat`). */
   clientTurnId?: string | null;
   language?: string | null;
   outcome?: ConciergeOutcome | string | null;
   sources?: string[] | null;
   receptionContact?: ConciergeReceptionContact | null;
   /** Not part of the final contract; still tolerated if present. */
   suggestReception?: boolean | null;
   schemaVersion?: number | null;
   /** Structured layout of an assistant answer (schemaVersion 2+). Render it when present; `message` is its plain-text form. */
   blocks?: ConciergeAnswerBlock[] | null;
   /** The hotel's photos of the places an answer cites, grouped by place (schemaVersion 3+). */
   photos?: ConciergeAnswerPhoto[] | null;
}

/**
 * Shape of a `ReceiveMessageDraft` event: the answer so far, pushed while the concierge is still writing it.
 *
 * Each draft carries every block so far for its turn (it replaces the previous one, and may be shorter when the
 * server restarts the answer). A draft has no id, outcome, sources, photos or reception contact; the final answer
 * still arrives as `ReceiveMessage` with the same `turnId` and `clientTurnId`. Drafts are optional.
 */
export interface ConciergeMessageDraftResponse {
   /** SERVER turn id. NOT equal to `clientTurnId`. */
   turnId?: string | null;
   /** The guest message's `clientTurnId`. */
   clientTurnId?: string | null;
   language?: string | null;
   /** Plain-text rendering of the blocks so far (the fallback when there are none). */
   message?: string | null;
   blocks?: ConciergeAnswerBlock[] | null;
}

/** One photo beside an answer: the hotel's own photo of a place the answer cites. */
export interface ConciergeAnswerPhoto {
   /** "restaurant", "dish", "amenity", "room" or "other". */
   kind?: string | null;
   /** The name of the place it shows. */
   title?: string | null;
   /** An absolute https address. */
   url?: string | null;
   altText?: string | null;
   caption?: string | null;
   width?: number | null;
   height?: number | null;
   /** The place it shows, as a knowledge source id: a place's photos share it. */
   place?: string | null;
   /** The index in `blocks` of the block that first names the place. */
   block?: number | null;
}

/** One block of a structured assistant answer. */
export interface ConciergeAnswerBlock {
   /** "text", "heading" or "item". */
   kind?: string | null;
   text?: string | null;
   /** The part of an item's text to show in bold; always a substring of `text`. */
   value?: string | null;
}

/** `GET /api/chat` */
export interface ConciergeGetChatResponse {
   id?: string;
   messages?: ConciergeMessageResponse[] | null;
   createdAt?: string | Date;
   updatedAt?: string | Date | null;
   preferredLanguage?: string | null;
   version?: number | null;
   /** The hotel's display name for the chat's header. Not sent by the API today (PRODUCT.md, open); read when present. */
   hotelName?: string | null;
   /** Up to three example questions from the hotel's own content. Not sent by the API today; read when present. */
   suggestedQuestions?: string[] | null;
}

/**
 * `POST /api/chat` body. `clientTurnId` is a uuid v4 generated per send and
 * reused, with the same text, on retry. The response is `true` when the turn
 * was accepted or processed (a ProviderError reply arrives as an assistant
 * message); `false` or an HTTP error means the request itself failed.
 *
 * Retrying with the same `clientTurnId`: a completed turn replays its stored
 * reply, a turn in progress does nothing new, and a ProviderError turn is
 * re-run (at most 3 attempts) and gets a NEW assistant message.
 */
export interface ConciergeSendMessageRequest {
   content: string;
   clientTurnId: string;
   language?: string;
}

/** `POST /api/chat/speech`: what the guest said, to send as their message; empty when no words were heard. */
export interface ConciergeTranscribeSpeechResponse {
   text: string;
}

/**
 * `ErrorResponse.title` used by the server when the same `clientTurnId` is
 * re-sent with different text.
 */
export const CONFLICTING_CLIENT_TURN_TITLE = 'CONFLICTING_CLIENT_TURN';
